import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Register from './pages/Register';
import PublicLayout from './components/PublicLayout';
import LegalNotice from './pages/LegalNotice';
import PrivacyPolicy from './pages/PrivacyPolicy';
import CookiesPolicy from './pages/CookiesPolicy';
import Terms from './pages/Terms';
import Contact from './pages/Contact';
import Dashboard from './pages/Dashboard';
import MyContract from './pages/MyContract';
import Companies from './pages/Companies';
import Campaigns from './pages/Campaigns';
import DataUpload from './pages/DataUpload';
import MyData from './pages/MyData';
import Indicators from './pages/Indicators';
import SectorStats from './pages/SectorStats';
import Benchmark from './pages/Benchmark';
import Audit from './pages/Audit';
import Incidents from './pages/Incidents';
import AIAgent from './pages/AIAgent';
import ManualVersions from './pages/ManualVersions';
import CatalogList from './pages/CatalogList';
import CatalogProduct from './pages/CatalogProduct';
import MyPublications from './pages/MyPublications';
import MyAccesses from './pages/MyAccesses';
import AdminCatalog from './pages/AdminCatalog';
import { Loader2 } from 'lucide-react';

function ProtectedRoute({ children, requireAdmin = false, requireEmpresa = false }) {
  const { isAuthenticated, loading, isAdmin, isEmpresa } = useAuth();
  if (loading) return (<div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-aea-blue" /></div>);
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (requireAdmin && !isAdmin) return <Navigate to="/dashboard" replace />;
  if (requireEmpresa && !isEmpresa) return <Navigate to="/dashboard" replace />;
  return <Layout>{children}</Layout>;
}

function PublicRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return (<div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-aea-blue" /></div>);
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />

      <Route element={<PublicLayout />}>
        <Route path="/legal" element={<LegalNotice />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/cookies" element={<CookiesPolicy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/contact" element={<Contact />} />
      </Route>

      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/mi-contrato" element={<ProtectedRoute><MyContract /></ProtectedRoute>} />
      <Route path="/mis-datos" element={<ProtectedRoute requireEmpresa><MyData /></ProtectedRoute>} />
      <Route path="/subir-datos" element={<ProtectedRoute requireEmpresa><DataUpload /></ProtectedRoute>} />
      <Route path="/benchmark" element={<ProtectedRoute requireEmpresa><Benchmark /></ProtectedRoute>} />
      <Route path="/agente-ia" element={<ProtectedRoute><AIAgent /></ProtectedRoute>} />
      <Route path="/incidencias" element={<ProtectedRoute><Incidents /></ProtectedRoute>} />

      {/* Admin */}
      <Route path="/empresas" element={<ProtectedRoute><Companies /></ProtectedRoute>} />
      <Route path="/campanas" element={<ProtectedRoute><Campaigns /></ProtectedRoute>} />
      <Route path="/indicadores" element={<ProtectedRoute requireAdmin><Indicators /></ProtectedRoute>} />
      <Route path="/estadisticas" element={<ProtectedRoute><SectorStats /></ProtectedRoute>} />
      <Route path="/auditoria" element={<ProtectedRoute><Audit /></ProtectedRoute>} />
      <Route path="/manual" element={<ProtectedRoute><ManualVersions /></ProtectedRoute>} />

      <Route path="/catalogo" element={<ProtectedRoute><CatalogList /></ProtectedRoute>} />
      <Route path="/catalogo/:productId" element={<ProtectedRoute><CatalogProduct /></ProtectedRoute>} />
      <Route path="/mis-publicaciones" element={<ProtectedRoute requireEmpresa><MyPublications /></ProtectedRoute>} />
      <Route path="/mis-accesos" element={<ProtectedRoute requireEmpresa><MyAccesses /></ProtectedRoute>} />
      <Route path="/admin/catalogo" element={<ProtectedRoute requireAdmin><AdminCatalog /></ProtectedRoute>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" toastOptions={{ style: { fontFamily: 'Public Sans, sans-serif' } }} />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
